/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Popover,
    PopoverContent,
    PopoverBody,
    useDisclosure,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ListMenuContent} from '../list-menu/list-menu-content'
import {ListMenuTrigger} from '../list-menu/list-menu-trigger'

const ListMenuPopover = ({contentComponent, item, name, itemsKey, hoverDelay}) => {
    const initialFocusRef = useRef()
    const triggerRef = useRef()
    const menuRef = useRef()
    const {isOpen, onClose, onOpen} = useDisclosure()
    const ContentComponent = contentComponent || ListMenuContent
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu

    const popverOverlay = {
        '.chakra-popover__popper::after': {
            content: 'attr(name)',
            position: 'absolute',
            background: 'black',
            opacity: '0.5',
            width: '100%',
            height: '100vh'
        }
    }

    // Handle focus outside of both trigger and popover
    const handleBlur = () => {
        setTimeout(() => {
            const focused = document.activeElement
            const inTrigger = triggerRef.current?.contains(focused)
            const inMenu = menuRef.current?.contains(focused)

            if (!inTrigger && !inMenu) {
                onClose()
            }
        }, 10)
    }

    return (
        <Box onMouseLeave={onClose} sx={popverOverlay}>
            <Popover
                isLazy
                placement={'bottom-start'}
                initialFocusRef={initialFocusRef}
                onOpen={onOpen}
                onClose={onClose}
                isOpen={isOpen}
                variant="fullWidth"
            >
                <Fragment>
                    <ListMenuTrigger
                        item={item}
                        name={name}
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                        menuRef={menuRef}
                        triggerRef={triggerRef}
                        onBlur={handleBlur}
                        hoverDelay={hoverDelay}
                    />
                    {isOpen && (
                        <PopoverContent
                            ref={menuRef}
                            onBlur={handleBlur}
                            onMouseLeave={onClose}
                            data-testid="popover-menu"
                            {...baseStyle.popoverContent}
                            position={'relative'}
                        >
                            <PopoverBody {...baseStyle.popoverBody}>
                                <ContentComponent
                                    item={item}
                                    itemsKey={itemsKey}
                                    initialFocusRef={initialFocusRef}
                                    onClose={onClose}
                                />
                            </PopoverBody>
                        </PopoverContent>
                    )}
                </Fragment>
            </Popover>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    contentComponent: PropTypes.elementType,
    item: PropTypes.object,
    name: PropTypes.string,
    maxColumns: PropTypes.number,
    itemsKey: PropTypes.string,
    hoverDelay: PropTypes.number
}

export {ListMenuPopover}
